'use client';
import React, { createContext, useEffect, useState } from 'react';
import { AuthData } from 'auth-types';
import { usePathname, useRouter } from 'next/navigation';
import { role } from 'auth-types';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { EffectiveRole } from 'auth-types';
import { isPathPermittedForRole, resolveFirstPermittedRoute } from '@/services/nav/nav-item-map';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import {
  setEffectiveRole,
  setisLoggedIn,
  setRole,
  setUserEmail,
  setUserFirstName,
  setUserLastName,
} from '@/lib/slices/login-slice';
import AuthService from '@/services/auth/auth-service';
import Request from '@/services/util/request-service';
import EffectiveRoleService from '@/services/auth/effective-role-service';

type AuthContextType = {
  authData: AuthData;
  setAuthData: (data: any) => void;
};

const defaultValue: AuthContextType = {
  authData: { token: '' },
  setAuthData: () => {},
};

const AuthContext = createContext<AuthContextType>(defaultValue);

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const loggedIn = useAppSelector(() => store.getState().isLoggedIn.status);
  const userRole: role = useAppSelector(() => store.getState().isLoggedIn.role);
  const effectiveRole: EffectiveRole | null = useAppSelector(
    () => store.getState().isLoggedIn.effectiveRole,
  );

  const [authData, setAuthData] = useState<AuthData>(defaultValue.authData);

  useEffect(() => {
    dispatch(setisLoggedIn(AuthService.isLoggedIn()));
  }, []);

  useEffect(() => {
    if (loggedIn && userRole == '') {
      getUserInfo();
    }
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;

    const syncUserCourses = async () => {
      try {
        await UserCourseStorageService.retrieveCourseCookie();
      } catch (error) {
        console.error('Failed to refresh user course access state.', error);
      }
    };

    syncUserCourses();
  }, [loggedIn]);

  const failClosedAuthBootstrap = () => {
    AuthService.signOutReset();
    dispatch(setisLoggedIn(false));
    dispatch(setRole(''));
    dispatch(setEffectiveRole(null));
    dispatch(setUserFirstName('Student'));
    dispatch(setUserLastName(''));
    dispatch(setUserEmail(''));
    router.push('/');
  };

  const getUserInfo = async () => {
    try {
      const response = await (await Request.get('user/getUserBasicInfo'))?.json();
      if (!response || response.error || (!response.role && !response.effectiveRole)) {
        failClosedAuthBootstrap();
        return;
      }

      const nextEffectiveRole = (response.effectiveRole ?? null) as EffectiveRole | null;
      dispatch(setEffectiveRole(nextEffectiveRole));
      const comparableRole = nextEffectiveRole
        ? EffectiveRoleService.deriveLegacyComparableRole(nextEffectiveRole)
        : ((response.role as role) ?? '');
      if (!comparableRole) {
        failClosedAuthBootstrap();
        return;
      }

      dispatch(setRole(comparableRole));
      dispatch(setUserFirstName(response.first_name));
      dispatch(setUserLastName(response.last_name));
      dispatch(setUserEmail(response.email));
      return;
    } catch {
      failClosedAuthBootstrap();
      return;
    }
  };

  useEffect(() => {
    if (userRole !== '') {
      const normalizePath = (path: string): string => {
        if (!path) return '/';
        const cleaned = path.split('#')[0]?.split('?')[0]?.trim() ?? '/';
        if (cleaned.length > 1 && cleaned.endsWith('/')) {
          return cleaned.slice(0, -1);
        }
        return cleaned || '/';
      };

      const validPath = isPathPermittedForRole(pathname, userRole, effectiveRole);

      const portalRoot = EffectiveRoleService.resolvePortalRoot(effectiveRole, userRole);
      const onExpectedPortal = pathname === portalRoot || pathname.startsWith(`${portalRoot}/`);
      if (!onExpectedPortal) {
        router.push(portalRoot);
        return;
      }

      // If no valid path is found, redirect to the first route available for this role
      if (!validPath) {
        const targetRoute = resolveFirstPermittedRoute(userRole, effectiveRole);
        if (!targetRoute) {
          return;
        }

        if (normalizePath(pathname) !== normalizePath(targetRoute)) {
          router.push(targetRoute);
        }
      }
    }
  }, [userRole, effectiveRole, pathname, router]);

  return <AuthContext.Provider value={{ authData, setAuthData }}>{children}</AuthContext.Provider>;
}

export { AuthProvider, AuthContext };
